import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError, backendApi } from '@/lib/backendApi';

type DeviceInfo = {
  deviceType: 'mobile' | 'tablet' | 'desktop' | 'unknown';
  osName: string;
  browserName: string;
  isMobile: boolean;
};

type LocationInfo = {
  country: string | null;
  countryCode: string | null;
  region: string | null;
  city: string | null;
};

const HEARTBEAT_INTERVAL_MS = 5 * 60_000;

const parseDeviceInfo = (): DeviceInfo => {
  if (typeof navigator === 'undefined') {
    return {
      deviceType: 'unknown',
      osName: 'unknown',
      browserName: 'unknown',
      isMobile: false
    };
  }

  const ua = navigator.userAgent || '';
  const isIPad = /iPad/.test(ua);
  const isTablet = isIPad || /Tablet|PlayBook/.test(ua);
  const isMobile = /Mobi|Android/i.test(ua) && !isTablet;

  let deviceType: DeviceInfo['deviceType'] = 'desktop';
  if (isTablet) deviceType = 'tablet';
  if (isMobile) deviceType = 'mobile';
  if (!ua) deviceType = 'unknown';

  let osName = 'Other';
  if (/Windows NT/i.test(ua)) osName = 'Windows';
  else if (/Mac OS X/i.test(ua)) osName = 'macOS';
  else if (/Android/i.test(ua)) osName = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) osName = 'iOS';
  else if (/Linux/i.test(ua)) osName = 'Linux';

  let browserName = 'Other';
  if (/Chrome\/\d+/i.test(ua) && /Safari/i.test(ua)) browserName = 'Chrome';
  if (/Safari\/\d+/i.test(ua) && !/Chrome/i.test(ua)) browserName = 'Safari';
  if (/Firefox\/\d+/i.test(ua)) browserName = 'Firefox';
  if (/Edg\/\d+/i.test(ua)) browserName = 'Edge';
  if (/MSIE|Trident/i.test(ua)) browserName = 'IE';

  return {
    deviceType,
    osName,
    browserName,
    isMobile: deviceType === 'mobile' || deviceType === 'tablet'
  };
};

const fetchLocationInfo = async (): Promise<LocationInfo> => {
  if (typeof fetch === 'undefined') {
    return {
      country: null,
      countryCode: null,
      region: null,
      city: null
    };
  }

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const response = await fetch('https://ipapi.co/json/', { signal: controller.signal });
    clearTimeout(timeout);
    if (!response.ok) {
      return {
        country: null,
        countryCode: null,
        region: null,
        city: null
      };
    }
    const data = await response.json();
    return {
      country: data?.country_name ?? null,
      countryCode: data?.country ?? null,
      region: data?.region ?? null,
      city: data?.city ?? null
    };
  } catch (error) {
    console.warn('Unable to resolve location info for the session tracker', error);
    return {
      country: null,
      countryCode: null,
      region: null,
      city: null
    };
  }
};

export const useSessionTracker = () => {
  const { user } = useAuth();
  const sessionIdRef = useRef<string | null>(null);
  const heartbeatRef = useRef<number | null>(null);
  const sessionStartRef = useRef<number | null>(null);
  const isEndingRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!user?.id) {
      sessionIdRef.current = null;
      sessionStartRef.current = null;
      isEndingRef.current = false;
      if (heartbeatRef.current) {
        window.clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      return;
    }

    let isMounted = true;

    const endSession = async () => {
      if (!sessionIdRef.current || isEndingRef.current) return;
      isEndingRef.current = true;

      const startedAt = sessionStartRef.current ?? Date.now();
      const durationSeconds = Math.max(Math.round((Date.now() - startedAt) / 1000), 0);

      try {
        await backendApi.endUserSession(sessionIdRef.current, durationSeconds);
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          // Expected when user logs out and cookies are already cleared.
          return;
        }
        console.error('Failed to end tracked session', error);
      }

      if (heartbeatRef.current) {
        window.clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };

    const refreshLastActivity = async () => {
      if (!sessionIdRef.current) return;
      try {
        await backendApi.heartbeatUserSession(sessionIdRef.current);
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          // Expected after logout while timers/handlers are still draining.
          return;
        }
        console.warn('Unable to refresh session heartbeat', error);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshLastActivity();
      }
    };

    const startHeartbeat = () => {
      if (heartbeatRef.current) {
        window.clearInterval(heartbeatRef.current);
      }
      heartbeatRef.current = window.setInterval(() => {
        if (document.visibilityState !== 'visible') {
          return;
        }
        void refreshLastActivity();
      }, HEARTBEAT_INTERVAL_MS);
    };

    const startSession = async () => {
      const deviceInfo = parseDeviceInfo();
      const locationInfo = await fetchLocationInfo();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const connection = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection;

      const metadata = {
        screen: {
          width: window.screen?.width ?? null,
          height: window.screen?.height ?? null
        },
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        referrer: document.referrer || null,
        userAgent: navigator.userAgent
      };

      try {
        const data = await backendApi.startUserSession({
          deviceType: deviceInfo.deviceType,
          osName: deviceInfo.osName,
          browserName: deviceInfo.browserName,
          isMobile: deviceInfo.isMobile,
          country: locationInfo.country,
          countryCode: locationInfo.countryCode,
          region: locationInfo.region,
          city: locationInfo.city,
          timezone,
          language: navigator.language,
          sessionSource: document.referrer || 'direct',
          networkType: connection?.effectiveType ?? null,
          metadata
        });

        if (!isMounted) {
          return;
        }

        sessionIdRef.current = data.id;
        sessionStartRef.current = new Date(data.started_at).getTime();
        isEndingRef.current = false;
        startHeartbeat();
      } catch (error) {
        console.error('Failed to start tracked session', error);
        return;
      }
    };

    const handleBeforeUnload = () => {
      void endSession();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    void startSession();

    return () => {
      isMounted = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      void endSession();
    };
  }, [user?.id]);
};
